let createPushError = (provider, code, message) => ({
  type: 'push',
  message,
  code: provider ? `${provider} ${code}` : code,
});

const PUSH_ERRORS = {
  NoDevicesSynced: 'No devices synced',
  NoDevice: 'No device found',
  NoProviderConfigured: 'No provider configured for this platform',
  ProviderTokenError: 'Could not generate auth token for provider using supplied credentials',
  EMPTY_NOTIFICATION_BODY:
    'The notification body contained no content. Check the message payload and any Liquid in it.',
  PushInvalidKey: createPushError(null, 'Invalid key', 'Check that this is the correct JSON key and try again.'),
  PushInvalidCertificate: createPushError(
    null,
    'Invalid certificate',
    'Check that this is the correct .p8 file and try again.',
  ),
  APNS_BadCollapseId: createPushError(
    'APNs',
    '400 BadCollapseId',
    'The apns-collapse-id in your custom payload is invalid',
  ),
  APNS_BadDeviceToken: createPushError(
    'APNs',
    '400 BadDeviceToken',
    "Device token is invalid or doesn't work with this platform. Register a corrected token.",
  ),
  APNS_BadExpirationDate: createPushError(
    'APNs',
    '400 BadExpirationDate',
    'The apns-expiration value in your custom payload is invalid.',
  ),
  APNS_BadMessageId: createPushError(
    'APNs',
    '400 BadMessageId',
    'The apns-id value in your custom payload is invalid.',
  ),
  APNS_BadPriority: createPushError(
    'APNs',
    '400 BadPriority',
    'The apns-priority value in your custom payload is invalid.',
  ),
  APNS_BadTopic: createPushError('APNs', '400 BadTopic', 'Your bundle ID is invalid. Check your configuration.'),
  APNS_DeviceTokenNotForTopic: createPushError(
    'APNs',
    '400 DeviceTokenNotForTopic',
    'Device token is not registered with the current bundle ID. Check your configuration or register a corrected token.',
  ),
  APNS_DuplicateHeaders: createPushError(
    'APNs',
    '400 DuplicateHeaders',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_IdleTimeout: createPushError('APNs', '400 IdleTimeout', {
    default: 'Internal Customer.io Error. Please report to win@customer.io',
    delivery: 'Internal Customer.io Error. Please report to win@customer.io',
  }),
  APNS_MissingDeviceToken: createPushError(
    'APNs',
    '400 MissingDeviceToken',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_MissingTopic: createPushError(
    'APNs',
    '400 MissingTopic',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_PayloadEmpty: createPushError('APNs', '400 PayloadEmpty', 'The message payload is empty.'),
  APNS_TopicDisallowed: createPushError(
    'APNs',
    '400 TopicDisallowed',
    'Device token is not registered with the current bundle ID. Check your configuration or register a corrected token.',
  ),
  APNS_BadCertificate: createPushError(
    'APNs',
    '403 BadCertificate',
    'Your .p8 certificate is invalid. Check your configuration.',
  ),
  APNS_BadCertificateEnvironment: createPushError(
    'APNs',
    '403 BadCertificateEnvironment',
    'Check that your .p8 certificate is for the correct APNs environment.',
  ),
  APNS_ExpiredProviderToken: createPushError(
    'APNs',
    '403 ExpiredProviderToken',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_Forbidden: createPushError(
    'APNs',
    '403 Forbidden',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_InvalidProviderToken: createPushError(
    'APNs',
    '403 InvalidProviderToken',
    'Invalid APNS provider token. Reset the iOS push credentials by disabling and re-enabling push in your workspace settings.',
  ),
  APNS_MissingProviderToken: createPushError(
    'APNs',
    '403 MissingProviderToken',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_BadPath: createPushError('APNs', '404 BadPath', 'Internal Customer.io Error. Please report to win@customer.io'),
  APNS_MethodNotAllowed: createPushError(
    'APNs',
    '405 MethodNotAllowed',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  APNS_Unregistered: createPushError('APNs', '410 Unregistered', 'This user has uninstalled the application.'),
  APNS_PayloadTooLarge: createPushError(
    'APNs',
    '413 PayloadTooLarge',
    'Your message payload was too large. The limit is 4KB.',
  ),
  APNS_TooManyProviderTokenUpdates: createPushError('APNs', '429 TooManyProviderTokenUpdates', {
    default: 'Internal Customer.io Error. Please report to win@customer.io',
    delivery: 'Internal Customer.io Error. Please report to win@customer.io',
  }),
  APNS_TooManyRequests: createPushError('APNs', '429 TooManyRequests', {
    default: 'Rate limit for device exceeded. We will retry; check back soon.',
    delivery: 'Rate limit for device exceeded. We will retry; check back soon.',
  }),
  APNS_InternalServerError: createPushError('APNs', '500 InternalServerError', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  APNS_ServiceUnavailable: createPushError('APNs', '503 ServiceUnavailable', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  APNS_Shutdown: createPushError('APNs', '503 Shutdown', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  FCM_UNSPECIFIED_ERROR: createPushError(
    'FCM',
    'UNSPECIFIED_ERROR',
    'Internal Customer.io Error. Please report to win@customer.io',
  ),
  FCM_INVALID_ARGUMENT: createPushError('FCM', 'INVALID_ARGUMENT', (error) =>
    error?.meta?.full_error_message
      ? `${error.meta.full_error_message} Learn more about <a href="https://firebase.google.com/docs/reference/fcm/rest/v1/ErrorCode">error codes for FCM failure conditions</a>.`
      : 'Part of your notification was invalid. Please report this error to win@customer.io for more detail.',
  ),
  FCM_UNREGISTERED: createPushError('FCM', 'UNREGISTERED', 'This user has uninstalled the application.'),
  FCM_SENDER_ID_MISMATCH: createPushError(
    'FCM',
    'SENDER_ID_MISMATCH',
    'The device token is associated with a different project than your Firebase certificate. Register a corrected token or replace the certificate.',
  ),
  FCM_QUOTA_EXCEEDED: createPushError('FCM', 'QUOTA_EXCEEDED', {
    default: 'Rate limit for device exceeded. We will retry; check back soon.',
    delivery: 'Rate limit for device exceeded. We will retry; check back soon.',
  }),
  FCM_APNS_AUTH_ERROR: createPushError(
    'FCM',
    'APNS_AUTH_ERROR',
    'Check that your FCM iOS certificate matches the one in Customer.io.',
  ),
  FCM_UNAVAILABLE: createPushError('FCM', 'UNAVAILABLE', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  FCM_INTERNAL: createPushError('FCM', 'INTERNAL', {
    default: 'Internal Customer.io Error. We will retry; check back soon.',
    delivery: 'Internal Customer.io Error. We will retry; check back soon.',
  }),
  FCM_INVALID_TOKEN: createPushError(
    'FCM',
    'INVALID_TOKEN',
    'Invalid platform or token. Retry with another token or platform.',
  ),
  FCM_PERMISSION_DENIED: createPushError(
    'FCM',
    'PERMISSION_DENIED',
    'Please make sure that the <a href="https://console.developers.google.com/apis/api/fcm.googleapis.com/overview" target="_blank" rel="noopener noreferrer">Firebase Cloud Messaging API</a> is enabled in your Firebase config.',
  ),
};

export default PUSH_ERRORS;
